// 스킬 타임라인 형식(D1c, COMBAT-FEEL 2.10, PRD 3A.3): 스킬을 틱(60Hz) 단위 데이터로 적는다.
// 이 파일은 형식과 해금 레벨 상수만 담은 잎 모듈이다(growth.ts도 읽는다). 영웅별 데이터는 skills/<hero>.ts.
// 시간 값은 모두 틱, 길이는 m, 각도는 도(deg), 피해는 공격력 배율이다.

/** 스킬 해금 방식: free = 처음부터, tree = 성장 트리 노드 1랭크, level = 영웅 레벨 N 이상이면 자동 습득 */
export type SkillUnlock =
  | { readonly kind: 'free' }
  | { readonly kind: 'tree' }
  | { readonly kind: 'level'; readonly level: number };

/** 영웅 몸 이동. dash = 길이 동안 facing 방향으로 distance(m) 이동, jump = 높이 height(m) 포물선(마지막 틱 착지) */
export type SkillMotion =
  | { readonly kind: 'dash'; readonly distance: number }
  | { readonly kind: 'jump'; readonly height: number };

/** 몹 당김: fromTick 이상 toTick 미만 매 틱 반경 안 몹을 영웅 쪽으로 speed(m/s) 끈다(보스 제외) */
export interface SkillPull {
  readonly radius: number;
  readonly speed: number;
  readonly fromTick: number;
  readonly toTick: number;
}

/** 히트박스 모양 */
export type HitShape =
  /** 영웅 중심 원 */
  | { readonly kind: 'circle'; readonly radius: number }
  /** 영웅 전방 부채꼴(range는 몸 가장자리 포함 판정, arcDeg 전체 각) */
  | { readonly kind: 'cone'; readonly range: number; readonly arcDeg: number }
  /** 이동 경로: 매 틱 이전 위치 → 현재 위치의 선분(폭 width 전체 두께) */
  | { readonly kind: 'path'; readonly width: number }
  /** 투사체(세계 검기): 발사 순간의 길로 length(m) 날아간다. 폭 width. 날아가는 시간은 travelTicks */
  | { readonly kind: 'wave'; readonly length: number; readonly width: number; readonly travelTicks: number }
  /** 화살(c022 라이라): 발사 순간 facing으로 세계 투사체를 쏜다(game/arrows.ts, 매 틱 이동 판정). Ev.Arrow를 낸다 */
  | { readonly kind: 'arrow'; readonly arrow: ArrowSpec }
  /** 장판(c022 라이라): 바닥 지점에 화살비·덫·회오리를 깐다(game/arrows.ts). 세계 위치 큐(at: 'world')를 낸다 */
  | { readonly kind: 'zone'; readonly zone: ZoneSpec }
  /**
   * 전방 띠(C9 카엘 대지 가르기): 그 틱의 영웅 위치에서 facing으로 from~to(m) 구간 선분, 폭 width.
   * 여러 히트박스로 구간을 이어 붙이면 균열이 앞으로 번지는 판정이 된다(같은 스킬 안에서 대상당 한 번, freshPulse가 없으면)
   */
  | { readonly kind: 'line'; readonly from: number; readonly to: number; readonly width: number };

/** 화살 종류(Ev.Arrow d). 렌더·VFX가 큐 이름을 고른다: 0 lyra_shot · 1 lyra_shot_heavy · 2 lyra_pierce · 3 폭발 · 4 lyra_air_volley · 5 lyra_homing · 6 lyra_launch_shot · 7 회오리 */
export const ArrowKind = { Basic: 0, Heavy: 1, Pierce: 2, Explode: 3, Air: 4, Homing: 5, Launch: 6, Vortex: 7 } as const;
export type ArrowKindId = (typeof ArrowKind)[keyof typeof ArrowKind];

/** 화살 한 발(또는 부채꼴 여러 발)의 규격. 길이 m, 속도 m/s(영웅 시간) */
export interface ArrowSpec {
  readonly kind: ArrowKindId;
  readonly speed: number;
  readonly range: number;
  /** 판정 폭(m, 전체 두께) */
  readonly width: number;
  /** 관통: 길 위의 적을 모두 맞힌다. 아니면 처음 맞은 적에서 멈춘다 */
  readonly pierce: boolean;
  /** 한 번에 쏘는 수(부채꼴). 기본 1 */
  readonly count?: number;
  /** 부채꼴 전체 각(도). count > 1일 때 양 끝 사이 각 */
  readonly spreadDeg?: number;
  /** 착탄(적중·사거리 끝·벽) 원형 폭발. 넉백은 finisher(마무리 계열) 규칙을 쓴다 */
  readonly explode?: { readonly radius: number; readonly mult: number };
  /** 착탄 지점에 회오리(흡입 장판)를 남긴다 */
  readonly vortex?: ZoneSpec;
  /** 유도: count발이 각각 반경 homingRadius 안의 서로 다른 적을 겨눈다(발사 순간 방향 고정, 없으면 부채꼴) */
  readonly homingRadius?: number;
  /** 발사 높이(m). 렌더 표시용(판정은 바닥 평면). 기본 1.2 */
  readonly y?: number;
}

/** 장판 규격(c022 라이라). 반경·위치·다단·속박·흡입 */
export interface ZoneSpec {
  /** rain = 다단 낙하 · net = 덫(밟으면 한 번 터져 속박) · vortex = 흡입 다단 */
  readonly kind: 'rain' | 'net' | 'vortex';
  readonly radius: number;
  /** 놓는 곳: target = 자석 대상 자리(없으면 전방 offset), front = 전방 offset, hero = 영웅 발밑 */
  readonly at: 'target' | 'front' | 'hero';
  /** 전방 거리(m) */
  readonly offset: number;
  /** 다단 횟수(net은 1)와 간격(틱). 첫 판정은 놓은 뒤 every 틱 */
  readonly pulses: number;
  readonly every: number;
  /** 한 판정의 피해 배율(공격력 대비). 실행 때 트리·연계 배율을 곱한다 */
  readonly mult: number;
  /** net: 놓인 뒤 기다리는 최대 틱과 밟힘 반경(m) */
  readonly life?: number;
  readonly trigger?: number;
  /** net: 속박 틱(엘리트 저항 50%, 보스는 그로기 때만) */
  readonly root?: number;
  /** vortex: 반경 안 몹을 중심으로 끄는 속도(m/s, 보스 제외) */
  readonly pull?: number;
}

/** 띄움 부여(런처): 맞은 몹을 lift(m) 높이로 airTicks 동안 띄운다. pull(m) = 영웅 쪽으로 끌어올림 */
export interface LaunchGrant {
  readonly lift: number;
  readonly airTicks: number;
  readonly pull: number;
}

/** 히트박스가 맞은 몹에게 주는 상태 */
export interface HitGrants {
  /** 런처: 일반 몹을 즉시 띄운다(엘리트 저항·멧돼지 면역·보스 피해만은 규칙대로) */
  readonly launch?: LaunchGrant;
  /** 마무리 계열 타격(기존 낙화): 판정에 finisher 플래그를 주어 기존 띄우기·무거운 처치 반응을 쓴다 */
  readonly finisher?: boolean;
}

/** Ev.Slash 표시(렌더 참격): 반경·각도·마무리 여부. firstOnly면 히트박스가 여러 틱이어도 첫 틱에만 낸다 */
export interface SlashFx {
  readonly range: number;
  /** 전체 각(deg), 360이면 원 */
  readonly arcDeg: number;
  readonly finisher: boolean;
  readonly firstOnly?: boolean;
}

/**
 * 트리 랭크 5·캡스톤 추가 효과가 붙는 자리(docs/SKILL-TREE.md, growth.ts BurstKind). 그 효과를 배웠을 때만 실행된다.
 * dashWake = 돌진이 끝나거나 끊길 때 경로 · stormEye = 다단 판정의 마지막 틱 · aftershock = 착지 판정 뒤 · twinMoon = 검기 발사 뒤
 */
export type BurstSlot = 'dashWake' | 'stormEye' | 'aftershock' | 'twinMoon';

export interface SkillHitbox {
  /** 시작 틱(포함) */
  readonly startTick: number;
  /** 끝 틱(포함). 단발이면 startTick과 같다 */
  readonly endTick: number;
  /** 반복 간격(틱). 없으면 1(구간 안 매 틱). 단발 히트박스는 startTick = endTick */
  readonly everyTicks?: number;
  readonly shape: HitShape;
  /** 이 히트박스의 피해 배율(공격력 대비). 트리 랭크 배율·연계 배율은 실행 때 곱한다 */
  readonly mult: number;
  readonly grants?: HitGrants;
  readonly slash?: SlashFx;
  /** 이 히트박스의 마지막 실행 뒤 붙는 트리 추가 효과 */
  readonly burst?: BurstSlot;
  /**
   * 새 판정 묶음(C9): 이 히트박스의 첫 판정 전에 같은 스킬의 앞 판정이 맞힌 대상을 다시 맞힐 수 있게 한다.
   * 공중 난무의 착지 마무리처럼 다단 뒤에 오는 별도 한 방에 쓴다. 없으면 같은 스킬 안에서 대상당 한 번(반복 히트박스는 everyTicks 규칙)
   */
  readonly freshPulse?: boolean;
}

/** 큐 채널: vfx(연출) · sfx(소리) · camera(카메라). 이름만 적고 런타임 연결은 렌더·오디오가 이름으로 고른다 */
export type CueChannel = 'vfx' | 'sfx' | 'camera';
export interface SkillCue {
  readonly tick: number;
  readonly channel: CueChannel;
  readonly name: string;
  /**
   * 큐 위치(c022). hero(기본) = 타임라인 tick에 영웅 위치로 낸다. world = 타임라인 tick으로는 내지 않고, 세계 효과(장판 놓기·장판 다단·덫 발동·폭발 착탄)가
   * 그 지점에서 낸다(Ev.SkillCue flags & 1, x·z = 세계 지점). event는 그 효과의 어느 순간인지
   */
  readonly at?: 'hero' | 'world';
  readonly event?: 'place' | 'pulse' | 'snap' | 'impact';
}

/** 스킬 속성(기록·검사용). launcher는 grants.launch가 있는 히트박스를 가져야 한다 */
export interface SkillProps {
  /**
   * 슈퍼아머(C9): 스킬 중에는 보스 강타(heavy)에도 동작이 끊기지 않는다(피해는 받는다, 행동 아머 보호 시간만).
   * 일반 피격은 원래 모든 스킬이 버틴다(hero.hurt 행동 아머)
   */
  readonly superArmor?: boolean;
  readonly launcher?: boolean;
  readonly downChase?: boolean;
}

/** 스킬 번호 1..13(입력 SKILL_BTN_ALL, 쿨다운 배열 인덱스 id-1). 카엘은 1..8(C9: 6 공중 난무·7 대지 가르기·8 혈검 회전베기) */
export type SkillNo = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
/** 옛 고정 키(U·I·O·L·P = 스킬 1..5). 6번부터는 슬롯 키(1~6·Alt)만 쓴다('') */
export type SkillKey = 'U' | 'I' | 'O' | 'L' | 'P' | '';

export interface SkillTimeline {
  readonly id: SkillNo;
  readonly key: SkillKey;
  readonly name: string;
  /** 애니 클립 이름(렌더 매핑용) */
  readonly clip: string;
  readonly cooldown: number;
  readonly mpCost: number;
  /** 스킬 길이(틱) */
  readonly duration: number;
  /** 캔슬 구간 시작 비율. CANCEL_ZONE(0.4~0.6) 안이어야 한다 */
  readonly cancelAt: number;
  readonly unlock: SkillUnlock;
  readonly motion?: SkillMotion;
  readonly pull?: SkillPull;
  readonly hitboxes: readonly SkillHitbox[];
  readonly cues: readonly SkillCue[];
  readonly props: SkillProps;
}

/**
 * 스킬 자동 습득 레벨(growth.ts가 레벨로 랭크를 정한다). 런처 Lv5, 진홍 초승달 Lv10(PRD 3A.1, docs/SKILL-TREE.md 11절).
 * 타임라인(kael.ts)의 unlock과 같은 값이어야 하며 테스트가 맞춘다.
 */
export const SKILL_UNLOCK_LEVEL = { launcher: 5, crescent: 10, airDance: 15, groundSplit: 20, bloodSpin: 25 } as const;

/** 자동 습득 표: 스킬 번호와 습득 레벨. 레벨 오름차순. 해금 판정(unlock.ts)·growthMods·HUD가 모두 이 표를 읽는다 (C9: Lv15·20·25 = 스킬 6·7·8, content/heroes.ts 카엘 행) */
export const SKILL_AUTO_LEARN: readonly { readonly id: 4 | 5 | 6 | 7 | 8; readonly level: number }[] = [
  { id: 5, level: SKILL_UNLOCK_LEVEL.launcher },
  { id: 4, level: SKILL_UNLOCK_LEVEL.crescent },
  { id: 6, level: SKILL_UNLOCK_LEVEL.airDance },
  { id: 7, level: SKILL_UNLOCK_LEVEL.groundSplit },
  { id: 8, level: SKILL_UNLOCK_LEVEL.bloodSpin },
];
/** 처음부터 가진 스킬(번호 1..3). 비트 i = 스킬 i+1 */
export const SKILL_FREE_MASK = 0b00111;

/** 영웅 id(플레이 가능한 영웅). types.ts PlayableHeroId와 같은 값(잎 모듈이라 다시 적는다) */
export type SkillHeroId = 'kael' | 'lyra';
/** 자동 습득 표 한 줄 */
export interface AutoLearnRow { readonly id: number; readonly level: number }
/**
 * 라이라 자동 습득(c022, content/heroes.ts 라이라 행 = PRD 3A.1): 올려쏘기 Lv5 · 바람 화살비 Lv10 · 공중 연사 Lv15 · 그물 덫 Lv20 · 폭발 화살 Lv25 ·
 * 회오리 화살 Lv35 · 유도 화살 폭풍 Lv45 · 천궁 강하 Lv55. 스킬 번호는 습득 순서(4..11). 타임라인(lyra.ts) unlock과 같아야 한다(테스트)
 */
export const LYRA_AUTO_LEARN: readonly AutoLearnRow[] = [
  { id: 4, level: 5 }, { id: 5, level: 10 }, { id: 6, level: 15 }, { id: 7, level: 20 },
  { id: 8, level: 25 }, { id: 9, level: 35 }, { id: 10, level: 45 }, { id: 11, level: 55 },
];
/** 영웅별 자동 습득 표·무료 스킬·스킬 수. 해금 판정(unlock.ts)·growthMods·HUD·슬롯이 읽는다 */
export const HERO_SKILL_TABLE: Readonly<Record<SkillHeroId, { readonly autoLearn: readonly AutoLearnRow[]; readonly freeMask: number; readonly count: number }>> = {
  // C10-5: 카엘 스킬 수 10(9·10 = 전직 스킬, 해금은 전직 영수증 kept 비트 — content/jobQuest.ts)
  kael: { autoLearn: SKILL_AUTO_LEARN, freeMask: SKILL_FREE_MASK, count: 10 },
  // C10-6: 라이라 스킬 수 13(12·13 = 전직 스킬, 해금은 전직 영수증 kept 비트 — content/jobQuest.ts)
  lyra: { autoLearn: LYRA_AUTO_LEARN, freeMask: 0b00111, count: 13 },
};
